// Stitch symbol definitions and utilities - Based on Craft Yarn Council Standards
// https://www.craftyarncouncil.com/standards/knit-chart-symbols
//
// To add new symbols:
// 1. Add the symbol to STITCH_SYMBOLS object
// 2. Add the full details to SYMBOL_KEY array with symbol, meaning, category, and description
// 3. The modal and other components will automatically pick up the new symbols!

export const STITCH_SYMBOLS = {
  // Basic Stitches - Using actual Craft Yarn Council symbols
  "◊": "Knit", // Diamond shape (K on RS, p on WS)
  "●": "Purl", // Dot (P on RS, k on WS)

  // Decreases
  "◢": "K2Tog", // Right-leaning decrease
  "◣": "SSK", // Left-leaning decrease
  "◤": "Sl1, K2Tog, PSSO", // Central double decrease
  "◥": "P2Tog", // Purl decrease

  // Increases
  "○": "Yarn Over", // Circle for yarn over
  "◐": "Make 1", // Make 1 knitwise
  "◑": "Make 1", // Make 1 purlwise

  // Slip Stitches
  "◈": "Slip 1", // Slip stitch symbol

  // Cable Symbols
  "◄": "Cable Right", // Right-leaning cable
  "►": "Cable Left", // Left-leaning cable

  // Special Stitches
  "◆": "Bobble", // Diamond for bobble
  "◘": "Nupp", // Bullet for nupp
};

export const SYMBOL_KEY = [
  // Basic Stitches
  {
    symbol: "◊",
    abbreviation: "K",
    meaning: "Knit",
    category: "Basics",
    description:
      "The most fundamental stitch. Insert needle from front to back, wrap yarn, pull through.",
    reverseSymbol: "●", // Knit on RS = Purl on WS
  },
  {
    symbol: "●",
    abbreviation: "P",
    meaning: "Purl",
    category: "Basics",
    description:
      "The reverse of knit. Insert needle from back to front, wrap yarn, pull through.",
    reverseSymbol: "◊", // Purl on RS = Knit on WS
  },
  {
    symbol: "⧄",
    abbreviation: "NS",
    meaning: "No Stitch",
    category: "Basics",
    description: "Empty space in pattern, no stitch worked.",
    reverseSymbol: null, // No stitch has no reverse
  },

  // Increases
  {
    symbol: "○",
    abbreviation: "YO",
    meaning: "Yarn Over",
    category: "Increases",
    description:
      "Creates a new stitch by wrapping yarn around the needle without knitting.",
    reverseSymbol: "○", // Yarn over is the same on both sides
  },
  {
    symbol: "◐",
    abbreviation: "M1L",
    meaning: "Make 1 Left",
    category: "Increases",
    description:
      "Increase by picking up the bar between stitches and knitting it left-slanting.",
    reverseSymbol: "◑", // M1L on RS = M1R on WS (reversed perspective)
  },
  {
    symbol: "◑",
    abbreviation: "M1R",
    meaning: "Make 1 Right",
    category: "Increases",
    description:
      "Increase by picking up the bar between stitches and knitting it right-slanting.",
    reverseSymbol: "◐", // M1R on RS = M1L on WS (reversed perspective)
  },
  {
    symbol: "↱",
    abbreviation: "KFB",
    meaning: "Knit Front & Back",
    category: "Increases",
    description: "Increase by knitting into front and back of same stitch.",
    reverseSymbol: "↳", // KFB on RS = PFB on WS
  },
  {
    symbol: "↳",
    abbreviation: "PFB",
    meaning: "Purl Front & Back",
    category: "Increases",
    description: "Increase by purling into front and back of same stitch.",
    reverseSymbol: "↱", // PFB on RS = KFB on WS
  },

  // Decreases
  {
    symbol: "◢",
    abbreviation: "K2tog",
    meaning: "Knit 2 Together",
    category: "Decreases",
    description:
      "Right-leaning decrease. Knit two stitches together from the left needle.",
    reverseSymbol: "◥", // K2Tog on RS = P2Tog on WS
  },
  {
    symbol: "◣",
    abbreviation: "SSK",
    meaning: "Slip Slip Knit",
    category: "Decreases",
    description:
      "Left-leaning decrease. Slip two stitches, then knit them together through back loops.",
    reverseSymbol: "◥", // SSK on RS = P2Tog on WS (SSP would be ideal but we use P2Tog)
  },
  {
    symbol: "◤",
    abbreviation: "SK2P",
    meaning: "Slip 1, K2Tog, PSSO",
    category: "Decreases",
    description:
      "Central double decrease. Slip 1, knit 2 together, pass slipped stitch over.",
    reverseSymbol: "◤", // Central double decrease stays the same (worked the same way)
  },
  {
    symbol: "◥",
    abbreviation: "P2tog",
    meaning: "Purl 2 Together",
    category: "Decreases",
    description:
      "Purl decrease. Purl two stitches together from the left needle.",
    reverseSymbol: "◢", // P2Tog on RS = K2Tog on WS
  },
  {
    symbol: "◧",
    abbreviation: "K3tog",
    meaning: "Knit 3 Together",
    category: "Decreases",
    description: "Triple decrease. Knit three stitches together.",
    reverseSymbol: "◨", // K3Tog on RS = P3Tog on WS
  },
  {
    symbol: "◨",
    abbreviation: "P3tog",
    meaning: "Purl 3 Together",
    category: "Decreases",
    description: "Triple purl decrease. Purl three stitches together.",
    reverseSymbol: "◧", // P3Tog on RS = K3Tog on WS
  },

  // Cables
  {
    symbol: "◄",
    abbreviation: "C2R",
    meaning: "Cable 2 Right",
    category: "Cables",
    description:
      "2-stitch right cable. Sl 1 st to cn and hold to back, k1, k1 from cn.",
    reverseSymbol: "►", // Cable Right on RS = Cable Left on WS (reversed perspective)
  },
  {
    symbol: "►",
    abbreviation: "C2L",
    meaning: "Cable 2 Left",
    category: "Cables",
    description:
      "2-stitch left cable. Sl 1 st to cn and hold to front, k1, k1 from cn.",
    reverseSymbol: "◄", // Cable Left on RS = Cable Right on WS (reversed perspective)
  },
  {
    symbol: "◀",
    abbreviation: "C3R",
    meaning: "Cable 3 Right",
    category: "Cables",
    description:
      "3-stitch right cable. Sl 1 st to cn and hold to back, k2, k1 from cn.",
    reverseSymbol: "▶", // Cable 3 Right on RS = Cable 3 Left on WS
  },
  {
    symbol: "▶",
    abbreviation: "C3L",
    meaning: "Cable 3 Left",
    category: "Cables",
    description:
      "3-stitch left cable. Sl 2 sts to cn and hold to front, k1, k2 from cn.",
    reverseSymbol: "◀", // Cable 3 Left on RS = Cable 3 Right on WS
  },
  {
    symbol: "◁",
    abbreviation: "C4R",
    meaning: "Cable 4 Right",
    category: "Cables",
    description:
      "4-stitch right cable. Sl 2 sts to cn and hold to back, k2, k2 from cn.",
    reverseSymbol: "▷", // Cable 4 Right on RS = Cable 4 Left on WS
  },
  {
    symbol: "▷",
    abbreviation: "C4L",
    meaning: "Cable 4 Left",
    category: "Cables",
    description:
      "4-stitch left cable. Sl 2 sts to cn and hold to front, k2, k2 from cn.",
    reverseSymbol: "◁", // Cable 4 Left on RS = Cable 4 Right on WS
  },

  // Lace
  {
    symbol: "◇",
    abbreviation: "YO+SK",
    meaning: "Yarn Over + Slip Knit",
    category: "Lace",
    description: "Yarn over followed by slip stitch, common in lace patterns.",
    reverseSymbol: "◇", // Yarn over + slip stays the same (slip is direction-independent)
  },
  {
    symbol: "◆",
    abbreviation: "YO+K2",
    meaning: "Yarn Over + K2tog",
    category: "Lace",
    description:
      "Yarn over followed by knit 2 together, creates eyelet in lace.",
    reverseSymbol: "◆", // Yarn over combinations typically stay the same
  },
  {
    symbol: "⬘",
    abbreviation: "YO+SSK",
    meaning: "Yarn Over + SSK",
    category: "Lace",
    description:
      "Yarn over followed by slip slip knit, creates eyelet in lace.",
    reverseSymbol: "⬘", // Yarn over combinations typically stay the same
  },

  // Texture
  {
    symbol: "◈",
    abbreviation: "SL1",
    meaning: "Slip 1",
    category: "Texture",
    description: "Move a stitch from left to right needle without working it.",
    reverseSymbol: "◈", // Slip stitch stays the same
  },
  {
    symbol: "◘",
    abbreviation: "BOB",
    meaning: "Bobble",
    category: "Texture",
    description:
      "A decorative raised stitch created by working multiple times into one stitch.",
    reverseSymbol: "◘", // Bobbles are worked the same way
  },
  {
    symbol: "◙",
    abbreviation: "NUPP",
    meaning: "Nupp",
    category: "Texture",
    description: "A small decorative bobble, often used in lace knitting.",
    reverseSymbol: "◙", // Nupps are worked the same way
  },

  // Other
  {
    symbol: "✶",
    abbreviation: "MARK",
    meaning: "Marker",
    category: "Other",
    description: "Place marker for reference point in pattern.",
    reverseSymbol: "✶", // Markers stay the same
  },
  {
    symbol: "Ʇ",
    abbreviation: "TBL",
    meaning: "Through Back Loop",
    category: "Other",
    description: "Knit or purl through the back loop of the stitch.",
    reverseSymbol: "Ʇ", // TBL works the same (though the base stitch may change)
  },
];

export const DEFAULT_SYMBOL = "◊"; // Knit stitch

export const isValidSymbol = (symbol) => {
  return Object.keys(STITCH_SYMBOLS).includes(symbol);
};

export const getSymbolMeaning = (symbol) => {
  return STITCH_SYMBOLS[symbol] || "Unknown";
};

export const getSymbolsByCategory = () => {
  const categories = {};
  SYMBOL_KEY.forEach((item) => {
    if (!categories[item.category]) {
      categories[item.category] = [];
    }
    categories[item.category].push(item);
  });
  return categories;
};

// Get all symbols with their full details
export const getAllSymbols = () => {
  return SYMBOL_KEY;
};

// Get symbols by specific category
export const getSymbolsByCategoryName = (categoryName) => {
  return SYMBOL_KEY.filter((item) => item.category === categoryName);
};

// Get symbol details by symbol
export const getSymbolDetails = (symbol) => {
  return SYMBOL_KEY.find((item) => item.symbol === symbol);
};

// Get the reverse/opposite symbol for wrong-side rows
// Returns the symbol itself if no reverse is defined
export const getReverseSymbol = (symbol) => {
  const symbolDetails = getSymbolDetails(symbol);
  if (symbolDetails && symbolDetails.reverseSymbol) {
    return symbolDetails.reverseSymbol;
  }
  // If no reverse defined, return the original symbol
  return symbol;
};

// Get all categories
export const getCategories = () => {
  return [...new Set(SYMBOL_KEY.map((item) => item.category))];
};

// Get symbols used in current pattern
export const getSymbolsUsedInPattern = (chart) => {
  if (!chart || !chart.rows) return [];

  const uniqueSymbols = new Set();

  // Iterate through all rows in the chart
  Object.values(chart.rows).forEach((row) => {
    if (Array.isArray(row)) {
      row.forEach((cell) => {
        if (cell && cell !== " " && cell !== "") {
          // Only exclude truly empty values
          uniqueSymbols.add(cell);
        }
      });
    }
  });

  const result = Array.from(uniqueSymbols)
    .map((symbol) => {
      const details = getSymbolDetails(symbol);
      return details;
    })
    .filter((symbol) => symbol !== undefined);

  return result;
};
